import type { Language } from '@eformulieren/engine'
import { type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { BusyButton } from './BusyButton.js'
import { ConfirmQuestion } from './ConfirmQuestion.js'
import { type LoginOption, LoginChoice, Outage } from './LoginChoice.js'
import { INVITATION_VALID_DAYS, formatDay } from './shareModel.js'
import type { Bilingual } from '../language/text.js'

/**
 * De pagina achter de link uit een uitnodiging (R-64c, R-139, R-140, R-155e, besluiten 4 en 5 van
 * 30 oktober 2026, audit G-06, G-07 en G-08, mockups 04 en 05 van delen, ontwerp 4.8 en 9.2).
 *
 * **Na inloggen staat er wat de uitnodiging geeft.** De pagina vraagt de pod de uitnodiging te tonen
 * zonder haar te verbruiken (`POST /uitnodiging/inzien`), en die doet dat alleen als het adres in het
 * token gelijk is aan dat van de uitnodiging. Dan staat hier van wie ze komt (als rol, besluit 5),
 * voor welk formulier, wat u per sectie kunt en wat niet, tot wanneer, en met welk account u bent
 * ingelogd. Kan de pod dat niet tonen (een pod van vóór fase 2), dan de keuze zoals in fase 1, zonder
 * die gegevens.
 *
 * **Bij advies** staan er geen secties: wat een adviseur ziet vóór zijn belangenverklaring blijft wat
 * fase 1 toont, de vraag en het onderwerp (open punt). De pagina zegt dat de verklaring eerst komt.
 *
 * **Niet bruikbaar: één melding met wat er aan de hand kan zijn en wat nu** (R-64c). Een onbekende,
 * gebruikte of andermans code ziet er hetzelfde uit, zodat de pagina niet verraadt welke codes
 * bestaan. Pas na inloggen met het adres van de uitnodiging zegt ze waarom: verlopen, geweigerd,
 * ingetrokken of al aangenomen (besluit 4).
 *
 * Weigeren vraagt een bevestiging, aannemen niet: weigeren is niet terug te draaien (de code is
 * daarna niet meer te gebruiken), en aannemen geeft alleen toegang die de deler al bedoelde.
 *
 * **Zonder sessie een knop per ingang naar de inlogdienst**, met "Inloggen" en "Ik werk bij NWO"
 * (`LoginChoice` voor de uitnodiging). Na het inloggen stuurt die terug naar deze pagina, en die stelt
 * dan vanzelf vast wie er kijkt.
 */

/** Wat de pod over de uitnodiging zei, in de woorden voor het scherm. */
export interface InvitationDetails {
  /** De titel van het formulier in de taal van de lezer. */
  readonly formulier: string
  readonly uitgenodigdDoor: 'aanvrager' | 'gemachtigde' | 'medewerker-nwo'
  readonly doel: 'deling' | 'advies'
  /** De titels van de secties om te schrijven en om te lezen. */
  readonly schrijven: readonly string[]
  readonly lezen: readonly string[]
  readonly geldigTot: string
  readonly ingelogdAls: string
}

/** Waarom een uitnodiging niet meer bruikbaar is; alleen na inloggen met het juiste adres. */
export type UnusableReason = 'verlopen' | 'geweigerd' | 'ingetrokken' | 'al-aangenomen' | 'vervallen'

export type InvitationState =
  /** Bezig met vaststellen wie er kijkt. */
  | { readonly soort: 'wachten' }
  /** Geen sessie: eerst inloggen, langs een van deze ingangen. */
  | { readonly soort: 'inloggen'; readonly opties: readonly LoginOption[] }
  /** Niet vast te stellen of er een sessie is (de pod of zijn identiteitsprovider is niet te bereiken). */
  | { readonly soort: 'storing' }
  /**
   * De keuze. Met `uitnodiging` als de pod haar kon tonen; met `opties` om met een ander account in te
   * loggen; met een melding als een vorige poging niet doorging.
   */
  | {
      readonly soort: 'keuze'
      readonly bezig?: boolean
      readonly melding?: string
      readonly uitnodiging?: InvitationDetails
      readonly opties?: readonly LoginOption[]
    }
  /** Niet bruikbaar, zonder reden (R-64c); met het account als dat bekend is. */
  | { readonly soort: 'ongeldig'; readonly ingelogdAls?: string; readonly opties?: readonly LoginOption[] }
  /** Niet meer bruikbaar, met de reden (besluit 4). */
  | { readonly soort: 'onbruikbaar'; readonly reden: UnusableReason; readonly geldigTot?: string; readonly opties?: readonly LoginOption[] }
  /**
   * Een adviesvraag voor de andere kant dan de ingang waarlangs is ingelogd: `intern` is binnen NWO
   * (ingelogd zonder NWO-account), `extern` buiten NWO (ingelogd met een NWO-account).
   */
  | { readonly soort: 'andere-ingang'; readonly kant: 'intern' | 'extern'; readonly opties: readonly LoginOption[] }
  /** Door de ontvanger zelf geweigerd. */
  | { readonly soort: 'geweigerd' }

export interface InvitationChoiceProps {
  readonly taal: Language
  readonly stand: InvitationState
  readonly onAccept: () => void
  readonly onReject: () => void
  /** Na een storing opnieuw vaststellen wie er kijkt. */
  readonly onAgain: () => void
  /**
   * De knop "Uitloggen" van de pagina, naast "met een ander account inloggen" bij "Niet u?" (R-154,
   * R-64c). Een slot, want uitloggen is een verzoek aan de inlogdienst en dat kent dit pakket niet.
   * Zonder slot alleen de ingangen.
   */
  readonly uitloggen?: ReactNode
}

const T = {
  titel: { nl: 'Uitnodiging', en: 'Invitation' },
  uitleg: {
    nl: 'U bent uitgenodigd om mee te werken aan een formulier. Neemt u de uitnodiging aan, dan krijgt u toegang met de rechten die voor u zijn gekozen. Weigert u, dan vervalt de uitnodiging.',
    en: 'You have been invited to work on a form. If you accept the invitation, you get access with the rights chosen for you. If you decline, the invitation lapses.',
  },
  meeschrijven: { nl: 'U bent uitgenodigd om mee te schrijven', en: 'You have been invited to co-write' },
  meelezen: { nl: 'U bent uitgenodigd om mee te lezen', en: 'You have been invited to read along' },
  advies: { nl: 'U bent gevraagd om advies', en: 'You have been asked for advice' },
  intro: { nl: '{wie} van {formulier} nodigt u uit voor dit formulier bij NWO.', en: '{wie} of {formulier} invites you to this form at NWO.' },
  introAdvies: { nl: '{wie} vraagt u om advies over {formulier}.', en: '{wie} asks for your advice on {formulier}.' },
  feiten: { nl: 'Wat deze uitnodiging geeft', en: 'What this invitation gives' },
  door: { nl: 'Uitgenodigd door', en: 'Invited by' },
  schrijven: { nl: 'U kunt schrijven in', en: 'You can write in' },
  lezen: { nl: 'U kunt lezen', en: 'You can read' },
  niet: { nl: 'Wat u niet kunt', en: 'What you cannot do' },
  nietTekst: { nl: 'Andere secties zien, indienen, of zelf iemand uitnodigen', en: 'See other sections, submit, or invite anyone yourself' },
  nietAdvies: { nl: 'Iets wijzigen in het formulier, of zelf iemand uitnodigen', en: 'Change anything in the form, or invite anyone yourself' },
  watEerst: { nl: 'Wat eerst komt', en: 'What comes first' },
  watEerstTekst: {
    nl: 'Na aannemen ziet u de vraag en verklaart u of u een belang hebt bij deze aanvraag. Pas daarna ziet u wat er met u is gedeeld.',
    en: 'After accepting you see the question and declare whether you have an interest in this application. Only then do you see what has been shared with you.',
  },
  geldig: { nl: 'Geldig tot', en: 'Valid until' },
  account: { nl: 'U bent ingelogd als {email}. Na aannemen staat wat u doet op dit e-mailadres.', en: 'You are signed in as {email}. After accepting, what you do is recorded under this email address.' },
  nietU: { nl: 'Niet u? Log in met een ander account:', en: 'Not you? Sign in with another account:' },
  nietUUitloggen: { nl: 'Niet u? Log uit, of log in met een ander account:', en: 'Not you? Sign out, or sign in with another account:' },
  alAangenomenAnder: {
    nl: 'Hebt u deze uitnodiging niet zelf aangenomen? Log uit, of log in met het account waarmee u haar aannam:',
    en: 'Did you not accept this invitation yourself? Sign out, or sign in with the account you accepted it with:',
  },
  aannemen: { nl: 'Aannemen', en: 'Accept' },
  aannemenFormulier: { nl: 'Aannemen en naar het formulier', en: 'Accept and go to the form' },
  aannemenAdvies: { nl: 'Aannemen en naar de adviesvraag', en: 'Accept and go to the request for advice' },
  aannemenBezig: { nl: 'Bezig met aannemen…', en: 'Accepting…' },
  weigeren: { nl: 'Weigeren', en: 'Decline' },
  weigerenBezig: { nl: 'Bezig met weigeren…', en: 'Declining…' },
  annuleren: { nl: 'Annuleren', en: 'Cancel' },
  weigerVraag: { nl: 'Uitnodiging weigeren?', en: 'Decline the invitation?' },
  weigerGevolg: {
    nl: 'Daarna kunt u deze uitnodiging niet meer aannemen. Wie u uitnodigde, kan u opnieuw uitnodigen.',
    en: 'After this you can no longer accept this invitation. Whoever invited you can invite you again.',
  },
  wachten: { nl: 'Bezig met vaststellen wie u bent.', en: 'Checking who you are.' },
  inloggen: {
    nl: 'Om deze uitnodiging te bekijken, moet u eerst inloggen. Daarna ziet u van wie ze komt en wat u kunt; de uitnodiging blijft in dit tabblad bewaard.',
    en: 'To view this invitation, you first need to sign in. Afterwards you see who it is from and what you can do; the invitation is kept in this tab.',
  },
  nietMeer: { nl: 'Deze uitnodiging kunt u niet meer gebruiken', en: 'You can no longer use this invitation' },
  redenen: { nl: 'Dat kan vier redenen hebben:', en: 'There can be four reasons:' },
  reden1: { nl: 'ze is verlopen: een uitnodiging is {d} dagen geldig;', en: 'it has expired: an invitation is valid for {d} days;' },
  reden2: { nl: 'ze is al aangenomen of geweigerd;', en: 'it has already been accepted or declined;' },
  reden3: { nl: 'wie u uitnodigde heeft haar ingetrokken;', en: 'whoever invited you has withdrawn it;' },
  reden4: { nl: 'ze is bedoeld voor een ander e-mailadres dan waarmee u bent ingelogd.', en: 'it is meant for another email address than the one you signed in with.' },
  reden4Met: {
    nl: 'ze is bedoeld voor een ander e-mailadres dan waarmee u bent ingelogd (u bent ingelogd als {email}).',
    en: 'it is meant for another email address than the one you signed in with (you are signed in as {email}).',
  },
  watNu: { nl: 'Wat nu', en: 'What now' },
  nieuwe: {
    nl: 'Vraag degene die u uitnodigde om een nieuwe uitnodiging. Hebt u haar al aangenomen, open het formulier dan via de link in uw eerste e-mail; inloggen is genoeg.',
    en: 'Ask whoever invited you for a new invitation. If you already accepted it, open the form through the link in your first email; signing in is enough.',
  },
  anderAccount: { nl: 'Met een ander account inloggen:', en: 'Sign in with another account:' },
  verlopen: { nl: 'Deze uitnodiging is verlopen', en: 'This invitation has expired' },
  verlopenTekst: { nl: 'Ze was geldig tot {d}.', en: 'It was valid until {d}.' },
  verlopenZonder: { nl: 'Een uitnodiging is {d} dagen geldig.', en: 'An invitation is valid for {d} days.' },
  geweigerdTitel: { nl: 'U hebt deze uitnodiging geweigerd', en: 'You have declined this invitation' },
  geweigerdTekst: { nl: 'Een geweigerde uitnodiging is niet meer aan te nemen.', en: 'A declined invitation can no longer be accepted.' },
  ingetrokken: { nl: 'Deze uitnodiging is ingetrokken', en: 'This invitation has been withdrawn' },
  ingetrokkenTekst: { nl: 'Wie u uitnodigde, heeft haar ingetrokken.', en: 'Whoever invited you has withdrawn it.' },
  alAangenomen: { nl: 'U hebt deze uitnodiging al aangenomen', en: 'You have already accepted this invitation' },
  alAangenomenTekst: {
    nl: 'Open het formulier via de link in uw eerste e-mail; inloggen is genoeg.',
    en: 'Open the form through the link in your first email; signing in is enough.',
  },
  vraagNieuwe: { nl: 'Moet u toch meedoen, vraag dan degene die u uitnodigde om een nieuwe uitnodiging.', en: 'If you still need to take part, ask whoever invited you for a new invitation.' },
  nwo: { nl: 'Deze uitnodiging is voor iemand binnen NWO', en: 'This invitation is for someone within NWO' },
  nwoTekst: {
    nl: 'U bent ingelogd zonder NWO-account. Log in met uw NWO-account om de uitnodiging te bekijken.',
    en: 'You are signed in without an NWO account. Sign in with your NWO account to view the invitation.',
  },
  buiten: { nl: 'Deze uitnodiging is voor iemand buiten NWO', en: 'This invitation is for someone outside NWO' },
  buitenTekst: {
    nl: 'U bent ingelogd met een NWO-account. Log in zonder NWO-account om de uitnodiging te bekijken.',
    en: 'You are signed in with an NWO account. Sign in without an NWO account to view the invitation.',
  },
  vervallen: { nl: 'Deze uitnodiging is vervallen', en: 'This invitation has lapsed' },
  vervallenTekst: {
    nl: 'De toegang van wie u uitnodigde is beëindigd, en daarmee ook deze uitnodiging.',
    en: 'The access of whoever invited you has ended, and with it this invitation.',
  },
  geweigerd: {
    nl: 'U hebt de uitnodiging geweigerd. U kunt dit venster sluiten.',
    en: 'You have declined the invitation. You can close this window.',
  },
} as const satisfies Readonly<Record<string, Bilingual>>

const INVITER: Readonly<Record<InvitationDetails['uitgenodigdDoor'], Bilingual>> = {
  aanvrager: { nl: 'De aanvrager', en: 'The applicant' },
  gemachtigde: { nl: 'De gemachtigde van de aanvrager', en: "The applicant's authorised representative" },
  'medewerker-nwo': { nl: 'Een medewerker van NWO', en: 'An NWO employee' },
}

function headingOf(state: InvitationState, language: Language): string {
  switch (state.soort) {
    case 'keuze': {
      const details = state.uitnodiging
      if (details === undefined) return T.titel[language]
      if (details.doel === 'advies') return T.advies[language]
      return (details.schrijven.length > 0 ? T.meeschrijven : T.meelezen)[language]
    }
    case 'ongeldig':
      return T.nietMeer[language]
    case 'onbruikbaar':
      return { verlopen: T.verlopen, geweigerd: T.geweigerdTitel, ingetrokken: T.ingetrokken, 'al-aangenomen': T.alAangenomen, vervallen: T.vervallen }[state.reden][language]
    case 'andere-ingang':
      return state.kant === 'intern' ? T.nwo[language] : T.buiten[language]
    default:
      return T.titel[language]
  }
}

export function InvitationChoice({ taal: language, stand: state, onAccept, onReject, onAgain, uitloggen: logout }: InvitationChoiceProps): ReactNode {
  return (
    <section className="ef-uitnodiging" lang={language} aria-labelledby="ef-uitnodiging-titel">
      <h1 id="ef-uitnodiging-titel" className="ef-uitnodiging__titel">
        {headingOf(state, language)}
      </h1>
      <Content taal={language} stand={state} onAccept={onAccept} onReject={onReject} onAgain={onAgain} {...(logout === undefined ? {} : { uitloggen: logout })} />
    </section>
  )
}

/** "Met een ander account inloggen", langs de ingangen van de pod, met uitloggen ernaast als dat er is. */
function OtherAccount({
  taal: language,
  opties: options,
  tekst,
  uitloggen: logout,
}: {
  readonly taal: Language
  readonly opties: readonly LoginOption[] | undefined
  readonly tekst: string
  readonly uitloggen?: ReactNode
}): ReactNode {
  if ((options === undefined || options.length === 0) && logout === undefined) return null
  return (
    <div className="ef-uitnodiging__ander">
      <p className="ef-uitnodiging__tekst">{tekst}</p>
      {options === undefined || options.length === 0 ? null : <LoginChoice taal={language} opties={options} voor="uitnodiging" />}
      {logout === undefined ? null : <div className="ef-uitnodiging__uitloggen">{logout}</div>}
    </div>
  )
}

function WhatNow({ taal: language, children }: { readonly taal: Language; readonly children: ReactNode }): ReactNode {
  const id = useId()
  return (
    <section className="ef-uitnodiging__watnu" aria-labelledby={id}>
      <h2 id={id} className="ef-uitnodiging__subkop">
        {T.watNu[language]}
      </h2>
      {children}
    </section>
  )
}

function Content({ taal: language, stand: state, onAccept, onReject, onAgain, uitloggen: logout }: InvitationChoiceProps): ReactNode {
  switch (state.soort) {
    case 'wachten':
      return (
        <p role="status" className="ef-uitnodiging__tekst">
          {T.wachten[language]}
        </p>
      )
    case 'inloggen':
      return (
        <>
          <p className="ef-uitnodiging__tekst">{T.inloggen[language]}</p>
          <LoginChoice taal={language} opties={state.opties} voor="uitnodiging" />
        </>
      )
    case 'storing':
      return <Outage taal={language} onAgain={onAgain} />
    case 'ongeldig':
      return (
        <div role="alert" className="ef-uitnodiging__blok">
          <p className="ef-uitnodiging__tekst">{T.redenen[language]}</p>
          <ul className="ef-uitnodiging__redenen">
            <li>{T.reden1[language].replace('{d}', String(INVITATION_VALID_DAYS))}</li>
            <li>{T.reden2[language]}</li>
            <li>{T.reden3[language]}</li>
            <li>{state.ingelogdAls === undefined ? T.reden4[language] : T.reden4Met[language].replace('{email}', state.ingelogdAls)}</li>
          </ul>
          <WhatNow taal={language}>
            <p className="ef-uitnodiging__tekst">{T.nieuwe[language]}</p>
            <OtherAccount taal={language} opties={state.opties} tekst={T.anderAccount[language]} />
          </WhatNow>
        </div>
      )
    case 'onbruikbaar': {
      const text =
        state.reden === 'verlopen'
          ? state.geldigTot === undefined
            ? T.verlopenZonder[language].replace('{d}', String(INVITATION_VALID_DAYS))
            : T.verlopenTekst[language].replace('{d}', formatDay(state.geldigTot, language, true))
          : state.reden === 'geweigerd'
            ? T.geweigerdTekst[language]
            : state.reden === 'ingetrokken'
              ? T.ingetrokkenTekst[language]
              : state.reden === 'vervallen'
                ? T.vervallenTekst[language]
                : undefined
      return (
        <div role="alert" className="ef-uitnodiging__blok">
          {text === undefined ? null : <p className="ef-uitnodiging__tekst">{text}</p>}
          <WhatNow taal={language}>
            <p className="ef-uitnodiging__tekst">{state.reden === 'al-aangenomen' ? T.alAangenomenTekst[language] : T.vraagNieuwe[language]}</p>
            {/* Al aangenomen, en de pod stuurde niet door: dan nam iemand anders haar aan, of een
                ander account van deze persoon. "Niet u?" met uitloggen en de ingangen (R-64c). */}
            {state.reden === 'al-aangenomen' ? (
              <OtherAccount taal={language} opties={state.opties} tekst={T.alAangenomenAnder[language]} {...(logout === undefined ? {} : { uitloggen: logout })} />
            ) : null}
          </WhatNow>
        </div>
      )
    }
    case 'andere-ingang':
      return (
        <>
          <p role="alert" className="ef-uitnodiging__tekst">
            {state.kant === 'intern' ? T.nwoTekst[language] : T.buitenTekst[language]}
          </p>
          <LoginChoice taal={language} opties={state.opties.filter((option) => option.kant === state.kant)} voor="uitnodiging" />
        </>
      )
    case 'geweigerd':
      return (
        <p role="status" className="ef-uitnodiging__tekst">
          {T.geweigerd[language]}
        </p>
      )
    case 'keuze':
      return (
        <Choice
          taal={language}
          bezig={state.bezig ?? false}
          melding={state.melding}
          uitnodiging={state.uitnodiging}
          opties={state.opties}
          onAccept={onAccept}
          onReject={onReject}
          {...(logout === undefined ? {} : { uitloggen: logout })}
        />
      )
  }
}

function Details({ taal: language, uitnodiging: details }: { readonly taal: Language; readonly uitnodiging: InvitationDetails }): ReactNode {
  const who = INVITER[details.uitgenodigdDoor][language]
  const facts: readonly (readonly [string, string])[] = [
    [T.door[language], who],
    ...(details.doel === 'advies'
      ? ([[T.watEerst[language], T.watEerstTekst[language]]] as const)
      : ([
          ...(details.schrijven.length > 0 ? [[T.schrijven[language], details.schrijven.join(', ')] as const] : []),
          ...(details.lezen.length > 0 ? [[T.lezen[language], details.lezen.join(', ')] as const] : []),
        ] as const)),
    [T.niet[language], details.doel === 'advies' ? T.nietAdvies[language] : T.nietTekst[language]],
    [T.geldig[language], formatDay(details.geldigTot, language, true)],
  ]
  return (
    <>
      <p className="ef-uitnodiging__tekst">
        {(details.doel === 'advies' ? T.introAdvies : T.intro)[language].replace('{wie}', who).replace('{formulier}', details.formulier)}
      </p>
      <ul className="ef-uitnodiging__feiten" aria-label={T.feiten[language]}>
        {facts.map(([term, value]) => (
          <li key={term} className="ef-uitnodiging__feit">
            <span className="ef-uitnodiging__term">{term}</span> <span className="ef-uitnodiging__waarde">{value}</span>
          </li>
        ))}
      </ul>
    </>
  )
}

function Choice({
  taal: language,
  bezig: busy,
  melding: notice,
  uitnodiging: details,
  opties: options,
  onAccept,
  onReject,
  uitloggen: logout,
}: {
  readonly taal: Language
  readonly bezig: boolean
  readonly melding: string | undefined
  readonly uitnodiging: InvitationDetails | undefined
  readonly opties: readonly LoginOption[] | undefined
  readonly onAccept: () => void
  readonly onReject: () => void
  readonly uitloggen?: ReactNode
}): ReactNode {
  const [requests, setRequests] = useState(false)
  const rejectButton = useRef<HTMLButtonElement>(null)
  const [callback, setRestoreFocus] = useState(false)
  useEffect(() => {
    if (!callback) return
    rejectButton.current?.focus()
    setRestoreFocus(false)
  }, [callback])
  const acceptText = details === undefined ? T.aannemen[language] : details.doel === 'advies' ? T.aannemenAdvies[language] : T.aannemenFormulier[language]

  return (
    <>
      {details === undefined ? <p className="ef-uitnodiging__tekst">{T.uitleg[language]}</p> : <Details taal={language} uitnodiging={details} />}
      {details === undefined ? null : (
        <div className="ef-uitnodiging__account">
          <p className="ef-uitnodiging__tekst">{T.account[language].replace('{email}', details.ingelogdAls)}</p>
          <OtherAccount
            taal={language}
            opties={options}
            tekst={logout === undefined ? T.nietU[language] : T.nietUUitloggen[language]}
            {...(logout === undefined ? {} : { uitloggen: logout })}
          />
        </div>
      )}
      {requests ? (
        <ConfirmQuestion
          kop={T.weigerVraag[language]}
          consequence={T.weigerGevolg[language]}
          proceed={T.weigeren[language]}
          soort="gevaar"
          cancel={T.annuleren[language]}
          busy={busy}
          busyText={T.weigerenBezig[language]}
          onProceed={() => {
            // De vraag blijft staan en de knop erin wacht op de pod; daarna toont de pagina de
            // uitkomst (`BusyButton`).
            onReject()
          }}
          onCancel={() => {
            setRequests(false)
            setRestoreFocus(true)
          }}
        />
      ) : (
        <div className="ef-uitnodiging__acties">
          <button ref={rejectButton} type="button" className="ef-actieknop" data-soort="secundair" disabled={busy} onClick={() => setRequests(true)}>
            {T.weigeren[language]}
          </button>
          <BusyButton className="ef-actieknop" data-soort="primair" busy={busy} busyText={T.aannemenBezig[language]} onAction={onAccept}>
            {acceptText}
          </BusyButton>
        </div>
      )}
      {notice === undefined ? null : (
        <p role="alert" className="ef-veld__melding--hard">
          {notice}
        </p>
      )}
    </>
  )
}
